import HelpOutline from '@mui/icons-material/HelpOutlined';
import { Box, Paper, Stack, Typography } from '@mui/material';

import type { TimelineEnvelope } from '../../types';

import { CommunicationCard } from './CommunicationCard';
import { DocRefCard } from './DocRefCard';

interface EnvelopeRowProps {
  envelope: TimelineEnvelope;
  inThread?: boolean;
  /** Receives the full envelope so the parent can route DocRefs (drawer)
   *  vs. generated documents (direct download) vs. anything else. */
  onOpenEnvelope?: (envelope: TimelineEnvelope) => void;
}

/**
 * Dispatch one envelope to its kind-specific card. Encounters render via
 * `TimelineThread` (with children); they don't pass through this row.
 *
 * Per the GDN-938 handoff: unknown kinds (`payment_event`, `form`,
 * `medication_event` in v1) get a placeholder card so the timeline
 * doesn't crash when adapters land. Returning `null` would silently hide
 * server-emitted data.
 */
export function EnvelopeRow({ envelope, inThread, onOpenEnvelope }: EnvelopeRowProps) {
  switch (envelope.kind) {
    case 'document':
      return (
        <DocRefCard
          envelope={envelope}
          inThread={inThread}
          onOpen={onOpenEnvelope}
        />
      );
    case 'communication':
      return <CommunicationCard envelope={envelope} inThread={inThread} />;
    case 'encounter':
      // Encounter envelopes are thread roots; PatientTimelineView routes
      // them through TimelineThread. Render a noop here just in case.
      return null;
    default:
      return <UnknownKindFallback envelope={envelope} />;
  }
}

function UnknownKindFallback({ envelope }: { envelope: TimelineEnvelope }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden
        >
          <HelpOutline sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
            {envelope.kind}
            {envelope.subkind ? ` · ${envelope.subkind}` : ''}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            Unknown envelope kind — renderer not implemented yet.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
